import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaWhatsapp, FaPhone, FaBars, FaTimes, FaEnvelope, FaChevronRight } from 'react-icons/fa';
import logo from '../../assets/images/logo.svg';
import LanguageSwitcher from '../ui/LanguageSwitcher';

const Header = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  
  // Close mobile menu when route changes
  useEffect(() => {
    if (isOpen) {
      setIsOpen(false);
      document.body.style.overflow = 'auto';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
    // Prevent scrolling when menu is open
    document.body.style.overflow = isOpen ? 'auto' : 'hidden';
  };

  const navItems = [
    { name: t('header.nav.home'), href: '/' },
    { name: t('header.nav.about'), href: '/about' },
    { name: t('header.nav.products'), href: '/products' },
    { name: t('header.nav.certificates'), href: '/certificates' },
  ];

  return (
    <header className={`fixed w-full top-0 z-50 transition-all ${scrolled ? 'backdrop-blur-lg bg-dark/90 shadow-lg' : 'bg-dark'}`}>
      {/* Top bar with contact info */}
      <div className="bg-gradient-to-r from-dark-medium via-primary to-dark-medium py-2 px-4 shadow-inner-light metal-shine">
        <div className="container mx-auto flex justify-between items-center">
          <div className="hidden md:flex items-center space-x-4">
            <a href="tel:+905544946406" className="flex items-center text-light/80 hover:text-light transition-colors group">
              <div className="bg-dark-accent/50 p-1.5 rounded-full group-hover:bg-accent/20 transition-colors">
                <FaPhone className="text-xs" />
              </div>
              <span className="ml-2 text-sm">{t('header.contact.phone')}</span>
            </a>
            <span className="text-dark-accent/50">|</span>
            <a href="tel:44467980" className="flex items-center text-light/80 hover:text-light transition-colors group">
              <div className="bg-dark-accent/50 p-1.5 rounded-full group-hover:bg-accent/20 transition-colors">
                <FaPhone className="text-xs" />
              </div>
              <span className="ml-2 text-sm">{t('header.contact.phone2')}</span>
            </a>
            <a href="mailto:info@celiksac.com.tr" className="flex items-center text-light/80 hover:text-light transition-colors group">
              <div className="bg-dark-accent/50 p-1.5 rounded-full group-hover:bg-accent/20 transition-colors">
                <FaEnvelope className="text-xs" />
              </div>
              <span className="ml-2 text-sm">{t('header.contact.email')}</span>
            </a>
          </div>
          <div className="flex items-center space-x-4">
            <a 
              href="https://wa.me/905544946406" 
              className="flex items-center text-light/80 hover:text-light transition-colors group" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              <div className="bg-dark-accent/50 p-1.5 rounded-full group-hover:bg-accent/20 transition-colors">
                <FaWhatsapp className="text-sm" />
              </div>
              <span className="hidden md:inline ml-2 text-sm">{t('header.contact.whatsapp')}</span>
            </a>
            <div className="border-l border-dark-accent/30 h-6 mx-2"></div>
            <LanguageSwitcher />
          </div>
        </div>
      </div>
      
      {/* Main navigation */}
      <div className="container mx-auto px-4 py-4 flex justify-between items-center">
        <Link to="/" className="flex items-center group">
          <div className="h-12 w-12 bg-white rounded-full flex items-center justify-center overflow-hidden ring-2 ring-accent/20 group-hover:ring-accent transition-all">
            <img src={logo} alt="ÇelikSac Logo" className="h-12 w-auto transition-transform group-hover:scale-110" />
          </div>
          <span className="ml-3 text-xl font-display font-black tracking-wide text-light bg-gradient-to-r from-light to-secondary bg-clip-text text-transparent">ÇELİKSAÇ</span>
        </Link>
        
        {/* Mobile menu button */}
        <button 
          className="md:hidden p-2 rounded-full text-secondary hover:text-accent focus:outline-none transition-colors bg-dark-light/50 hover:bg-dark-accent/30"
          onClick={toggleMenu}
          aria-label={isOpen ? "Close menu" : "Open menu"}
        >
          {isOpen ? <FaTimes size={20} /> : <FaBars size={20} />}
        </button>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center space-x-1">
          {navItems.map((item) => (
            <Link
              key={item.name}
              to={item.href}
              className={`px-4 py-2 text-sm font-medium rounded-full transition-all hover:bg-dark-light relative group ${location.pathname === item.href ? 'text-accent bg-dark-medium' : 'text-light/80 hover:text-light'}`}
            >
              <span className="relative z-10">{item.name}</span>
              <span className={`absolute bottom-0 left-0 w-full h-0.5 ${location.pathname === item.href ? 'bg-accent' : 'bg-secondary opacity-0 group-hover:opacity-100'} rounded-full transform origin-left transition-all duration-300 ease-out`}></span>
            </Link>
          ))}
          <Link 
            to="/contact" 
            className="ml-2 px-5 py-2 bg-gradient-to-r from-accent to-accent/80 text-dark font-medium rounded-full hover:shadow-md hover:-translate-y-0.5 transition-all btn">
            {t('common.contactUs')} <FaChevronRight className="inline-block ml-1 text-xs" />
          </Link>
        </nav>
      </div>
      
      {/* Mobile Navigation */}
      {isOpen && (
        <div className="md:hidden bg-dark/95 backdrop-blur-lg fixed inset-0 top-[104px] z-50 h-screen overflow-y-auto animate-fadeIn border-t border-dark-accent/30">
          <div className="px-4 pt-6 pb-8 space-y-1">
            {navItems.map((item, index) => (
              <Link
                key={item.name}
                to={item.href}
                className={`flex items-center justify-between px-4 py-4 text-base font-medium rounded-lg transition-all animate-slideIn ${location.pathname === item.href ? 'text-accent bg-dark-accent/20' : 'text-light/80 hover:text-light hover:bg-dark-light/40'}`}
                style={{animationDelay: `${index * 50}ms`}}
                onClick={toggleMenu}
              >
                <span>{item.name}</span>
                <FaChevronRight className="text-xs opacity-50" />
              </Link>
            ))}
            
            <div className="mt-8 pt-8 border-t border-dark-accent/30 space-y-4">
              <div className="text-xs uppercase text-secondary/70 px-4 pb-2">{t('header.nav.contact')}</div>
              <a href="tel:+905544946406" className="flex items-center px-4 py-3 text-sm text-light/80 hover:text-light hover:bg-dark-accent/20 rounded-lg transition-all">
                <div className="bg-dark-accent/30 p-2 rounded-full mr-3">
                  <FaPhone className="text-accent text-xs" />
                </div>
                <span>{t('header.contact.phone')}</span>
              </a>
              <a href="tel:44467980" className="flex items-center px-4 py-3 text-sm text-light/80 hover:text-light hover:bg-dark-accent/20 rounded-lg transition-all">
                <div className="bg-dark-accent/30 p-2 rounded-full mr-3">
                  <FaPhone className="text-accent text-xs" />
                </div>
                <span>{t('header.contact.phone2')}</span>
              </a>
              <a href="mailto:info@celiksac.com.tr" className="flex items-center px-4 py-3 text-sm text-light/80 hover:text-light hover:bg-dark-accent/20 rounded-lg transition-all">
                <div className="bg-dark-accent/30 p-2 rounded-full mr-3">
                  <FaEnvelope className="text-accent text-xs" />
                </div>
                <span>{t('header.contact.email')}</span>
              </a>
              <a href="https://wa.me/905544946406" className="flex items-center px-4 py-3 text-sm text-light/80 hover:text-light hover:bg-dark-accent/20 rounded-lg transition-all" target="_blank" rel="noopener noreferrer">
                <div className="bg-dark-accent/30 p-2 rounded-full mr-3">
                  <FaWhatsapp className="text-accent text-xs" />
                </div>
                <span>{t('header.contact.whatsapp')}</span>
              </a>
              
              <div className="mt-8 px-4">
                <Link 
                  to="/contact" 
                  className="w-full block text-center px-5 py-3 bg-gradient-to-r from-accent to-accent/80 text-dark font-medium rounded-lg hover:shadow-md transition-all btn">
                  {t('common.contactUs')} <FaChevronRight className="inline-block ml-1 text-xs" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
